<template>
  <div
      ref="container"
      class="infinite-scroll"
  >
    <div v-if="isEmpty && !isLoading" class="infinite-scroll__empty">
      <slot name="empty">
        <p>{{ emptyText }}</p>
      </slot>
    </div>

    <slot/>

    <div v-if="isLoading" class="infinite-scroll__loading">
      <slot name="loading">
        <div class="spinner"/>
        <p>{{ loadingText }}</p>
      </slot>
    </div>


    <div v-else-if="!hasMore && !isEmpty" class="infinite-scroll__end">
      <slot name="end">
        <p>{{ endText }}</p>
      </slot>
    </div>


    <div ref="sentinel" class="infinite-scroll__sentinel"/>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Props {
  isLoading?: boolean;
  hasMore?: boolean;
  isEmpty?: boolean;
  disabled?: boolean;
  loadingText?: string;
  endText?: string;
  emptyText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  hasMore: true,
  isEmpty: false,
  disabled: false,
  loadingText: 'Loading…',
  endText: '',
  emptyText: 'Nothing to show yet',
});

const emit = defineEmits<{
  (e: 'loadMore'): void;
}>();

const container = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let pollingInterval: number | null = null;
let pendingTimeout: number | null = null;

const canLoadMore = () =>
    !props.disabled && !props.isLoading && props.hasMore && !props.isEmpty;

const isElementVisible = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();

  // Check if element is visible in the viewport
  // Element is considered visible if any part of it is in the viewport
  return rect.top < window.innerHeight && rect.bottom > 0;
};

const checkVisibility = () => {
  if (!sentinel.value || !canLoadMore()) {
    return;
  }

  if (isElementVisible(sentinel.value)) {
    // If visible, set a timeout to confirm it's still visible after 100ms
    pendingTimeout = window.setTimeout(() => {
      pendingTimeout = null;
      if (sentinel.value && canLoadMore() && isElementVisible(sentinel.value)) {
        emit('loadMore');
      }
    }, 200);
  }
};

const startPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
  }

  pollingInterval = window.setInterval(checkVisibility, 50);
};

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }

  if (pendingTimeout) {
    clearTimeout(pendingTimeout);
    pendingTimeout = null;
  }
};

onMounted(() => {
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
});

watch(
    () => [props.hasMore, props.disabled, props.isEmpty],
    () => {
      if (!props.hasMore || props.disabled || props.isEmpty) {
        stopPolling();
      } else if (!pollingInterval) {
        startPolling();
      }
    }
);
</script>

<style scoped>
.infinite-scroll {
  position: relative;
  width: 100%;
}

.infinite-scroll__sentinel {
  width: 100%;
  height: 1px;
}

.infinite-scroll__loading,
.infinite-scroll__end,
.infinite-scroll__empty {
  text-align: center;
  padding: 24px 16px;
  color: #666;
}

.infinite-scroll__empty {
  color: #999;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #667eea;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
